import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import * as am5percent from '@amcharts/amcharts5/percent';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';

// ─── Design tokens (shared system) ──────────────────────────────
const T = {
  bg:          '#ffffff',
  shadow:      '8px 8px 18px #cccce6, -8px -8px 18px #ffffff',
  shadowSm:    '5px 5px 12px #cccce6, -5px -5px 12px #ffffff',
  shadowInset: 'inset 4px 4px 10px #cccce6, inset -4px -4px 10px #ffffff',
  purple:      '#7c3aed',
  purpleMid:   '#6d28d9',
  purpleLight: '#ede9fe',
  purpleFaint: '#f5f3ff',
  textPrimary: '#1e1b4b',
  textSecond:  '#5b5e8b',
  textMuted:   '#9ca3b8',
  border:      'rgba(124,58,237,0.10)',
};

// amCharts purple palette
const CHART_COLORS = ['#7c3aed','#6d28d9','#8b5cf6','#a78bfa','#c4b5fd','#4c1d95','#5b21b6','#9333ea','#7e22ce','#6b21a8'];

// ─── SVG icons ───────────────────────────────────────────────────
const ICONS = {
  trending:  'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
  award:     'M12 15l-2 5-3-1 5-4zm0 0l2 5 3-1-5-4zm0 0V3m0 12c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6z',
  trophy:    'M8 21h8m-4-4v4m0-4a4 4 0 004-4H8a4 4 0 004 4zm8-14V3m-8 0v4m8 0H4m16 0a2 2 0 01-2 2H6a2 2 0 01-2-2',
  chart:     'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  zap:       'M13 10V3L4 14h7v7l9-11h-7z',
  globe:     'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9',
  users:     'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  activity:  'M22 12h-4l-3 9L9 3l-3 9H2',
  refresh:   'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  clock:     'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
};

function Icon({ name, size = 16, color = T.purple }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
      <path d={ICONS[name] || ICONS.chart} />
    </svg>
  );
}

function NmCard({ children, style = {}, hover = false }) {
  const [h, setH] = useState(false);
  return (
    <div onMouseEnter={() => hover && setH(true)} onMouseLeave={() => hover && setH(false)}
      style={{
        background: T.bg, borderRadius: 20,
        boxShadow: h ? T.shadow : T.shadowSm,
        transform: h ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'all 0.22s cubic-bezier(0.4,0,0.2,1)',
        ...style,
      }}>
      {children}
    </div>
  );
}

// ─── Rank badge ──────────────────────────────────────────────────
function RankBadge({ rank }) {
  const configs = {
    1: { bg: `linear-gradient(135deg, #a78bfa, ${T.purple})`,    shadow: `0 4px 12px rgba(124,58,237,0.4)` },
    2: { bg: `linear-gradient(135deg, #c4b5fd, #8b5cf6)`,        shadow: `0 4px 12px rgba(139,92,246,0.3)` },
    3: { bg: `linear-gradient(135deg, #ddd6fe, #a78bfa)`,        shadow: `0 4px 10px rgba(167,139,250,0.3)` },
  };
  const cfg = configs[rank];
  return (
    <div style={{
      width: 32, height: 32, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 12, fontWeight: 800, color: cfg ? '#fff' : T.textSecond,
      background: cfg ? cfg.bg : T.bg,
      boxShadow: cfg ? cfg.shadow : T.shadowInset,
    }}>
      {rank}
    </div>
  );
}

// ─── Horizontal bar (custom, no chart lib needed for leaderboard) ─
function MiniBar({ value, max = 100 }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ height: 5, background: T.purpleLight, borderRadius: 100, overflow: 'hidden', width: 80 }}>
      <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1, ease: [0.4,0,0.2,1] }}
        style={{ height: '100%', borderRadius: 100, background: `linear-gradient(90deg, ${T.purple}, ${T.purpleMid})` }} />
    </div>
  );
}

export function RankingsSection() {
  const [rankingsData,  setRankingsData]  = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [timeRange,     setTimeRange]     = useState('week');
  const [topModels,     setTopModels]     = useState([]);
  const [providerData,  setProviderData]  = useState([]);
  const [speedRankings, setSpeedRankings] = useState([]);
  const [costRankings,  setCostRankings]  = useState([]);

  const intelligenceChartRef = useRef(null);
  const pieChartRef          = useRef(null);
  const speedChartRef        = useRef(null);
  const costChartRef         = useRef(null);

  // ── amCharts: Intelligence bar chart ────────────────────────────
  useLayoutEffect(() => {
    if (!intelligenceChartRef.current || loading || !topModels.length) return;
    const root = am5.Root.new(intelligenceChartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    const chart = root.container.children.push(am5xy.XYChart.new(root, { panX: false, panY: false, wheelX: 'none', wheelY: 'none', paddingLeft: 0, paddingRight: 20 }));
    const yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(root, { categoryField: 'name', renderer: am5xy.AxisRendererY.new(root, { minGridDistance: 20, cellStartLocation: 0.1, cellEndLocation: 0.9 }), tooltip: am5.Tooltip.new(root, {}) }));
    yAxis.get('renderer').labels.template.setAll({ fontSize: 11, fill: am5.color(0x5b5e8b) });
    yAxis.data.setAll(topModels);
    const xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererX.new(root, {}), min: 0, max: 100 }));
    xAxis.get('renderer').labels.template.setAll({ fontSize: 11, fill: am5.color(0x9ca3b8) });
    const series = chart.series.push(am5xy.ColumnSeries.new(root, { name: 'Reasoning Score', xAxis, yAxis, valueXField: 'intelligence', categoryYField: 'name', tooltip: am5.Tooltip.new(root, { labelText: '{categoryY}: {valueX.formatNumber("#.0")}' }) }));
    series.columns.template.setAll({ cornerRadiusTR: 6, cornerRadiusBR: 6, strokeOpacity: 0, fillOpacity: 0.85 });
    series.columns.template.adapters.add('fill', (_, target) => am5.color(CHART_COLORS[(target.dataItem?.index || 0) % CHART_COLORS.length]));
    series.data.setAll(topModels);
    series.appear(1000);
    chart.appear(1000, 100);
    return () => root.dispose();
  }, [loading, topModels]);

  // ── amCharts: Provider donut ─────────────────────────────────────
  useLayoutEffect(() => {
    if (!pieChartRef.current || loading || !providerData.length) return;
    const root = am5.Root.new(pieChartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    const chart = root.container.children.push(am5percent.PieChart.new(root, { layout: root.verticalLayout, innerRadius: am5.percent(55) }));
    const series = chart.series.push(am5percent.PieSeries.new(root, { valueField: 'value', categoryField: 'provider', alignLabels: false }));
    series.labels.template.setAll({ textType: 'circular', centerX: 0, centerY: 0, fontSize: 11, fontWeight: '600', fill: am5.color(0x5b5e8b) });
    series.slices.template.setAll({ strokeWidth: 3, stroke: am5.color(0xffffff), cornerRadius: 6 });
    series.slices.template.adapters.add('fill', (_, target) => am5.color(CHART_COLORS[target.dataItem?.index || 0]));
    series.data.setAll(providerData);
    series.appear(1000, 100);
    return () => root.dispose();
  }, [loading, providerData]);

  // ── amCharts: Speed bar ──────────────────────────────────────────
  useLayoutEffect(() => {
    if (!speedChartRef.current || loading || !speedRankings.length) return;
    const root = am5.Root.new(speedChartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    const chart = root.container.children.push(am5xy.XYChart.new(root, { panX: false, panY: false, wheelX: 'none', wheelY: 'none', paddingLeft: 0, paddingRight: 20 }));
    const yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(root, { categoryField: 'name', renderer: am5xy.AxisRendererY.new(root, { minGridDistance: 20, cellStartLocation: 0.1, cellEndLocation: 0.9 }), tooltip: am5.Tooltip.new(root, {}) }));
    yAxis.get('renderer').labels.template.setAll({ fontSize: 11, fill: am5.color(0x5b5e8b) });
    yAxis.data.setAll(speedRankings);
    const xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererX.new(root, {}), min: 0 }));
    const series = chart.series.push(am5xy.ColumnSeries.new(root, { name: 'Speed (tok/s)', xAxis, yAxis, valueXField: 'speed', categoryYField: 'name', tooltip: am5.Tooltip.new(root, { labelText: '{categoryY}: {valueX} tok/s' }) }));
    series.columns.template.setAll({ cornerRadiusTR: 6, cornerRadiusBR: 6, strokeOpacity: 0, fillOpacity: 0.85, fill: am5.color(0x8b5cf6) });
    series.data.setAll(speedRankings);
    series.appear(1000);
    chart.appear(1000, 100);
    return () => root.dispose();
  }, [loading, speedRankings]);

  // ── amCharts: Cost bar ───────────────────────────────────────────
  useLayoutEffect(() => {
    if (!costChartRef.current || loading || !costRankings.length) return;
    const root = am5.Root.new(costChartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);
    const chart = root.container.children.push(am5xy.XYChart.new(root, { panX: false, panY: false, wheelX: 'none', wheelY: 'none', paddingLeft: 0, paddingRight: 20 }));
    const yAxis = chart.yAxes.push(am5xy.CategoryAxis.new(root, { categoryField: 'name', renderer: am5xy.AxisRendererY.new(root, { minGridDistance: 20, cellStartLocation: 0.1, cellEndLocation: 0.9 }), tooltip: am5.Tooltip.new(root, {}) }));
    yAxis.get('renderer').labels.template.setAll({ fontSize: 11, fill: am5.color(0x5b5e8b) });
    yAxis.data.setAll(costRankings);
    const xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, { renderer: am5xy.AxisRendererX.new(root, {}), min: 0 }));
    const series = chart.series.push(am5xy.ColumnSeries.new(root, { name: 'Price ($/1M)', xAxis, yAxis, valueXField: 'price', categoryYField: 'name', tooltip: am5.Tooltip.new(root, { labelText: '{categoryY}: ${valueX}/1M' }) }));
    series.columns.template.setAll({ cornerRadiusTR: 6, cornerRadiusBR: 6, strokeOpacity: 0, fillOpacity: 0.85, fill: am5.color(0x6d28d9) });
    series.data.setAll(costRankings);
    series.appear(1000);
    chart.appear(1000, 100);
    return () => root.dispose();
  }, [loading, costRankings]);

  useEffect(() => { fetchRankings(); }, [timeRange]);

  const fetchRankings = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://api.daggpt.network';
      const res  = await fetch(`${API_URL}/api/rankings`);
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setRankingsData(data);
      if (data.topModels?.length)          setTopModels(data.topModels);
      if (data.providerDistribution?.length) setProviderData(data.providerDistribution);
      if (data.speedRankings?.length)      setSpeedRankings(data.speedRankings);
      if (data.costRankings?.length)       setCostRankings(data.costRankings);
    } catch (e) {
      console.error('Rankings fetch error:', e);
    }
    setLoading(false);
  };

  // ── Loading state ────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '100px 0', flexDirection: 'column', gap: 14 }}>
      <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1.1, ease: 'linear' }}
        style={{ width: 44, height: 44, border: `3px solid ${T.purpleLight}`, borderTopColor: T.purple, borderRadius: '50%' }} />
      <div style={{ fontSize: 14, fontWeight: 600, color: T.textMuted, letterSpacing: 0.2 }}>Loading rankings data...</div>
    </div>
  );

  // ── Time range tabs ──────────────────────────────────────────────
  const TimeTab = ({ value, label }) => {
    const active = timeRange === value;
    return (
      <button onClick={() => setTimeRange(value)}
        style={{
          padding: '7px 16px', borderRadius: 100, border: 'none', cursor: 'pointer',
          fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
          background: active ? `linear-gradient(135deg, ${T.purple}, ${T.purpleMid})` : T.bg,
          color: active ? '#fff' : T.textSecond,
          boxShadow: active ? `0 4px 14px rgba(124,58,237,0.3)` : `inset 2px 2px 5px #d0d0e8, inset -2px -2px 5px rgba(255,255,255,0.65)`,
          transition: 'all 0.16s',
        }}>
        {label}
      </button>
    );
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22 }}
      style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <style>{`
        @media (max-width: 1024px) {
          .rankings-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .rankings-charts-grid-1 { grid-template-columns: 1fr !important; }
          .rankings-charts-grid-2 { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 640px) {
          .rankings-stats-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      {/* ── Page header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 14 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: T.textPrimary, margin: 0, letterSpacing: -0.5 }}>AI Model Rankings</h1>
          <p style={{ fontSize: 12, color: T.textMuted, margin: '5px 0 0', fontWeight: 500 }}>
            Live benchmarks · Data by{' '}
            <a href="https://artificialanalysis.ai" target="_blank" rel="noopener noreferrer"
              style={{ color: T.purple, textDecoration: 'none', fontWeight: 700 }}>Artificial Analysis</a>
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <TimeTab value="day"   label="24H" />
          <TimeTab value="week"  label="7D"  />
          <TimeTab value="month" label="30D" />
          <button onClick={fetchRankings}
            style={{ marginLeft: 4, width: 34, height: 34, borderRadius: 12, border: 'none', background: T.bg, boxShadow: T.shadowSm, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="refresh" size={14} color={T.textMuted} />
          </button>
        </div>
      </div>

      {/* ── Stat cards ── */}
      <div className="rankings-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 18 }}>
        {[
          { label: 'Total Models',    value: rankingsData?.stats?.totalModels  || '-', sub: `${rankingsData?.stats?.providers || '-'} providers`, icon: 'users',    accent: true },
          { label: 'Top Ranked',      value: rankingsData?.stats?.topModel     || '-', sub: 'Highest reasoning',                                   icon: 'trophy'             },
          { label: 'Avg Intelligence',value: rankingsData?.stats?.avgIntelligence || '-', sub: 'Composite benchmark',                              icon: 'activity'           },
          { label: 'Data Source',     value: 'Artificial Analysis',             sub: 'Live benchmarks',                                            icon: 'globe'              },
        ].map((s, i) => (
          <NmCard key={i} style={{ padding: '20px 22px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <div style={{
                width: 38, height: 38, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: s.accent ? `linear-gradient(135deg, ${T.purple}, ${T.purpleMid})` : T.bg,
                boxShadow: s.accent ? `0 5px 14px rgba(124,58,237,0.33)` : T.shadowSm,
              }}>
                <Icon name={s.icon} size={16} color={s.accent ? '#fff' : T.purple} />
              </div>
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: T.textMuted }}>{s.label}</span>
            </div>
            <div style={{ fontSize: s.value.length > 12 ? 14 : 22, fontWeight: 800, color: T.textPrimary, letterSpacing: -0.4, marginBottom: 3 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: T.textMuted }}>{s.sub}</div>
          </NmCard>
        ))}
      </div>

      {/* ── Charts row 1: Intelligence bar + Provider donut ── */}
      <div className="rankings-charts-grid-1" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 18 }}>
        <NmCard style={{ padding: '24px 26px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: T.purpleLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="chart" size={15} color={T.purple} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, letterSpacing: -0.2 }}>Top 10 by Reasoning Score</div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>Intelligence index benchmark</div>
            </div>
          </div>
          <div ref={intelligenceChartRef} style={{ width: '100%', height: 440 }} />
        </NmCard>

        <NmCard style={{ padding: '24px 26px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: T.purpleLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="globe" size={15} color={T.purple} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, letterSpacing: -0.2 }}>Provider Distribution</div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>Share of indexed models</div>
            </div>
          </div>
          <div ref={pieChartRef} style={{ width: '100%', height: 440 }} />
        </NmCard>
      </div>

      {/* ── Charts row 2: Speed + Cost ── */}
      <div className="rankings-charts-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
        <NmCard style={{ padding: '24px 26px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: T.purpleLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="zap" size={15} color={T.purple} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, letterSpacing: -0.2 }}>Fastest Models</div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>Tokens per second</div>
            </div>
          </div>
          <div ref={speedChartRef} style={{ width: '100%', height: 380 }} />
        </NmCard>

        <NmCard style={{ padding: '24px 26px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: T.purpleLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="activity" size={15} color={T.purple} />
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, letterSpacing: -0.2 }}>Most Cost-Efficient</div>
              <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>Price per million tokens</div>
            </div>
          </div>
          <div ref={costChartRef} style={{ width: '100%', height: 380 }} />
        </NmCard>
      </div>

      {/* ── Leaderboard table ── */}
      <NmCard style={{ overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '20px 26px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 10, background: `linear-gradient(135deg, ${T.purple}, ${T.purpleMid})`, boxShadow: `0 4px 12px rgba(124,58,237,0.3)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon name="trophy" size={15} color="#fff" />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.textPrimary, letterSpacing: -0.2 }}>Model Leaderboard</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>Ranked by intelligence index</div>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
            <thead>
              <tr style={{ background: T.purpleFaint }}>
                {['Rank', 'Model', 'Provider', 'Intelligence', 'Speed', 'Price / 1M'].map(h => (
                  <th key={h} style={{ padding: '11px 18px', textAlign: h === 'Rank' ? 'center' : 'left', fontSize: 10, fontWeight: 800, letterSpacing: 1, color: T.textMuted, textTransform: 'uppercase', borderBottom: `1px solid ${T.border}`, whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {topModels.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ padding: '48px 24px', textAlign: 'center', color: T.textMuted, fontSize: 13 }}>
                    No ranking data available
                  </td>
                </tr>
              ) : topModels.map((model, i) => (
                <motion.tr key={model.rank || i}
                  initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }}
                  style={{ borderBottom: `1px solid ${T.border}`, transition: 'background 0.14s' }}
                  onMouseEnter={e => e.currentTarget.style.background = T.purpleFaint}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>

                  <td style={{ padding: '13px 18px', textAlign: 'center' }}>
                    <RankBadge rank={model.rank || (i + 1)} />
                  </td>

                  <td style={{ padding: '13px 18px' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.textPrimary, letterSpacing: -0.1 }}>{model.name}</div>
                    {model.intelligence && <MiniBar value={model.intelligence} max={100} />}
                  </td>

                  <td style={{ padding: '13px 18px' }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: T.purple, background: T.purpleLight, padding: '3px 10px', borderRadius: 6, letterSpacing: 0.3 }}>
                      {model.provider}
                    </span>
                  </td>

                  <td style={{ padding: '13px 18px' }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: T.textPrimary, letterSpacing: -0.3 }}>{model.intelligenceText || '-'}</div>
                    <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>index</div>
                  </td>

                  <td style={{ padding: '13px 18px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.textSecond }}>{model.speedText || '-'}</div>
                  </td>

                  <td style={{ padding: '13px 18px' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.purple }}>{model.priceText || '-'}</div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Attribution footer */}
        <div style={{ padding: '14px 26px', borderTop: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 11, color: T.textMuted }}>
            Data sourced from{' '}
            <a href="https://artificialanalysis.ai" target="_blank" rel="noopener noreferrer" style={{ color: T.purple, fontWeight: 700, textDecoration: 'none' }}>
              Artificial Analysis
            </a>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: T.purple, boxShadow: `0 0 0 2px ${T.purpleLight}` }} />
            <span style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: 0.5, textTransform: 'uppercase' }}>Live data</span>
          </div>
        </div>
      </NmCard>
    </motion.div>
  );
}
